/**
 * LetterGrid — orchestrates letter generation, tile rendering,
 * selection logic, and state management.
 *
 * Uses a GridMask to define which cells exist (supports irregular shapes).
 */

import { cellKey } from "../../../../framework/geometry";
import { AudioManager } from "../../../../framework/AudioManager";
import { ADJ_MIN_LENGTH } from "../../../character/progression/WordBonuses";
import { GridConfig, GridLayout, DEFAULT_CONFIG, computeMaskLayout } from "./GridConfig";
import { GridMask } from "./GridMask";
import { generateMaskLetters, randomLetter, WordPicker, GenerateResult } from "./LetterPool";
import {
  TileState, SparseObjects, TILE_COLORS,
  createMaskTiles, destroyMaskTiles,
  styleTile, enableTile as enableTileVisual, disableTile as disableTileVisual,
} from "./TileRenderer";

export interface TileCoord {
  row: number;
  col: number;
}

export interface SelectedTile extends TileCoord {
  letter: string;
}

export class LetterGrid {
  private scene: any;
  private config: GridConfig;
  private mask: GridMask;
  private layout!: GridLayout;
  private centerX: number;
  private startY: number;

  private letters: Map<string, string> = new Map();
  private states: Map<string, TileState> = new Map();
  private objects!: SparseObjects;
  private selected: SelectedTile[] = [];
  private onSelectionChange: (() => void) | null = null;
  private _locked = false;
  private wordPicker?: WordPicker;
  private _seededWords: string[] = [];

  constructor(
    scene: any,
    centerX: number,
    startY: number,
    mask: GridMask,
    config?: Partial<GridConfig>,
    wordPicker?: WordPicker,
  ) {
    this.scene = scene;
    this.centerX = centerX;
    this.startY = startY;
    this.mask = mask;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.wordPicker = wordPicker;
  }

  /** Generate letters and create display objects. */
  create(onSelectionChange: () => void): void {
    this.onSelectionChange = onSelectionChange;
    this.registerTones();
    this.build();
  }

  /** Rebuild the grid with a new mask. */
  resize(mask: GridMask): void {
    this.selected = [];
    destroyMaskTiles(this.objects);
    this.mask = mask;
    this.build();
  }

  // --- Public API ---

  getSelection(): SelectedTile[] {
    return this.selected;
  }

  getWord(): string {
    return this.selected.map((t) => t.letter).join("");
  }

  clearSelection(): void {
    for (const t of this.selected) {
      const key = cellKey(t);
      const entry = this.objects.map.get(key);
      const state = this.states.get(key);
      if (entry && state) styleTile(entry.tile, state);
    }
    this.selected = [];
    this.onSelectionChange?.();
  }

  disableUsedTiles(): TileCoord[] {
    const disabled: TileCoord[] = [];
    for (const t of this.selected) {
      const key = cellKey(t);
      const entry = this.objects.map.get(key);
      if (!entry) continue;
      this.states.set(key, "disabled");
      disableTileVisual(entry.tile, entry.text);
      styleTile(entry.tile, "disabled");
      disabled.push({ row: t.row, col: t.col });
    }
    this.selected = [];
    this.onSelectionChange?.();
    return disabled;
  }

  /** Refresh specific tiles with random letters (no seeding — used for resurrect perk). */
  refreshTiles(tiles: TileCoord[]): void {
    for (const t of tiles) {
      const key = cellKey(t);
      const letter = randomLetter();
      this.letters.set(key, letter);
      const entry = this.objects.map.get(key);
      if (entry) {
        entry.text.setText(letter);
        this.enableTile(t.row, t.col);
      }
    }
  }

  /** Refresh all disabled tiles with new seeded+random letters. */
  refreshDisabled(): void {
    const disabled: TileCoord[] = [];
    for (const [key, state] of this.states.entries()) {
      if (state === "disabled") {
        const [r, c] = key.split(",").map(Number);
        disabled.push({ row: r, col: c });
      }
    }
    this.refreshWithSeeding(disabled);
  }

  /** Replace the entire grid with new seeded+random letters. */
  refreshAll(): void {
    this.refreshWithSeeding(this.mask.toArray());
  }

  /** Regenerate letters for a set of tiles using seeding + random fill. */
  private refreshWithSeeding(tiles: TileCoord[]): void {
    if (tiles.length === 0) return;

    // Build a sub-mask from the tiles being refreshed
    const subMask = new GridMask(tiles);
    const result = generateMaskLetters(subMask, this.wordPicker);
    this._seededWords = result.seededWords;

    for (const t of tiles) {
      const key = cellKey(t);
      const letter = result.letters.get(key) ?? randomLetter();
      this.letters.set(key, letter);
      const entry = this.objects.map.get(key);
      if (entry) {
        entry.text.setText(letter);
        this.enableTile(t.row, t.col);
      }
    }
  }

  freezeTile(row: number, col: number): void {
    const key = cellKey({ row, col });
    this.states.set(key, "frozen");
    const entry = this.objects.map.get(key);
    if (entry) {
      entry.tile.disableInteractive();
      styleTile(entry.tile, "frozen");
    }
  }

  activeCount(): number {
    let count = 0;
    for (const state of this.states.values()) {
      if (state === "active") count++;
    }
    return count;
  }

  hide(): void {
    for (const entry of this.objects.map.values()) {
      entry.tile.setVisible(false);
      entry.text.setVisible(false);
      entry.tile.removeInteractive();
    }
  }

  get bounds(): { centerX: number; top: number; bottom: number; width: number; height: number } {
    return {
      centerX: this.layout.originX + this.layout.totalW / 2,
      top: this.layout.originY,
      bottom: this.layout.originY + this.layout.totalH,
      width: this.layout.totalW,
      height: this.layout.totalH,
    };
  }

  flashInvalid(): void {
    for (const t of this.selected) {
      const entry = this.objects.map.get(cellKey(t));
      if (entry) entry.tile.setFillStyle(TILE_COLORS.invalid.fill);
    }
    this.scene.time.delayedCall(300, () => {
      for (const t of this.selected) {
        const entry = this.objects.map.get(cellKey(t));
        if (entry) entry.tile.setFillStyle(TILE_COLORS.selected.fill);
      }
    });
  }

  /** Words seeded into the grid during generation. */
  get seededWords(): string[] {
    return this._seededWords;
  }

  /** Margin above the grid. */
  static get margin(): number {
    return 12;
  }

  // --- Internal ---

  private build(): void {
    this.layout = computeMaskLayout(
      this.mask,
      this.config.tileSize,
      this.config.tileGap,
      this.centerX,
      this.startY,
    );
    const result = generateMaskLetters(this.mask, this.wordPicker);
    this.letters = result.letters;
    this._seededWords = result.seededWords;
    this.states = this.initStates();
    this.objects = createMaskTiles(
      this.scene,
      this.mask,
      this.layout,
      this.config.tileSize,
      this.config.tileGap,
      this.letters,
      (r, c) => this.toggleTile(r, c),
    );
  }

  private initStates(): Map<string, TileState> {
    const states = new Map<string, TileState>();
    for (const cell of this.mask.toArray()) {
      states.set(cellKey(cell), "active");
    }
    return states;
  }

  /** Lock/unlock the grid — when locked, no tiles can be selected. */
  set locked(value: boolean) { this._locked = value; }
  get locked(): boolean { return this._locked; }

  private toggleTile(row: number, col: number): void {
    if (this._locked) return;
    const key = cellKey({ row, col });
    if (this.states.get(key) !== "active") return;

    const audio = this.scene.game.registry.get("audio") as AudioManager | undefined;
    const idx = this.selected.findIndex((t) => t.row === row && t.col === col);

    if (idx !== -1) {
      const from = idx === this.selected.length - 1 ? idx : idx + 1;
      const removed = this.selected.splice(from);
      for (let i = 0; i < removed.length; i++) {
        const rKey = cellKey(removed[i]);
        const entry = this.objects.map.get(rKey);
        const state = this.states.get(rKey);
        if (entry && state) styleTile(entry.tile, state);
        const pitch = (this.selected.length + removed.length - 1 - i) * 20;
        if (i === 0) {
          audio?.playTone("tile_deselect", pitch);
        } else {
          this.scene.time.delayedCall(i * 40, () => audio?.playTone("tile_deselect", pitch));
        }
      }
    } else {
      const letter = this.letters.get(key) ?? "?";
      this.selected.push({ row, col, letter });
      audio?.playTone("tile_select", (this.selected.length - 1) * 20);
    }

    this.styleSelectedTiles();
    this.onSelectionChange?.();
  }

  /** Restyle all selected tiles: green if fully adjacent chain, blue otherwise. */
  private styleSelectedTiles(): void {
    const sel = this.selected;

    // Check if the entire chain qualifies for adjacency highlight
    let fullyAdjacent = sel.length >= ADJ_MIN_LENGTH;
    if (fullyAdjacent) {
      for (let i = 1; i < sel.length; i++) {
        if (!this.isAdjacent(sel[i - 1], sel[i])) {
          fullyAdjacent = false;
          break;
        }
      }
    }

    const colors = fullyAdjacent ? TILE_COLORS.adjacent : TILE_COLORS.selected;
    for (const t of sel) {
      const entry = this.objects.map.get(cellKey(t));
      if (!entry) continue;
      entry.tile.setFillStyle(colors.fill);
      entry.tile.setStrokeStyle(2, colors.stroke);
    }
  }

  private isAdjacent(a: { row: number; col: number }, b: { row: number; col: number }): boolean {
    const dr = Math.abs(a.row - b.row);
    const dc = Math.abs(a.col - b.col);
    return dr <= 1 && dc <= 1 && (dr + dc) > 0;
  }

  private enableTile(row: number, col: number): void {
    const key = cellKey({ row, col });
    this.states.set(key, "active");
    const entry = this.objects.map.get(key);
    if (entry) {
      enableTileVisual(entry.tile, entry.text);
      styleTile(entry.tile, "active");
    }
  }

  private registerTones(): void {
    const audio = this.scene.game.registry.get("audio") as AudioManager | undefined;
    if (!audio) return;
    audio.registerTone("tile_select", { frequency: 280, duration: 0.04, type: "sine", volume: 0.08, slideTo: 340 });
    audio.registerTone("tile_deselect", { frequency: 260, duration: 0.03, type: "sine", volume: 0.05, slideTo: 200 });
  }
}
